// DemoModeProvider.jsx - Modal optimisé mobile Samsung Galaxy A54/A55
import { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, UserPlus, Shield, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const DemoModeContext = createContext();

export function useDemoMode() {
  return useContext(DemoModeContext);
}

export function DemoModeProvider({ children }) {
  const navigate = useNavigate();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authAction, setAuthAction] = useState('');
  
  const isAuthenticated = useCallback(() => {
    return !!(localStorage.getItem('ecosystem_token') || localStorage.getItem('admin_token'));
  }, []);
  
  const requireAuth = useCallback((action = 'accéder à cette fonctionnalité') => {
    if (isAuthenticated()) {
      return true;
    }
    setAuthAction(action);
    setShowAuthModal(true);
    return false;
  }, [isAuthenticated]);
  
  const goToRegister = () => {
    setShowAuthModal(false);
    navigate('/reseau/inscription');
  };
  
  const goToLogin = () => {
    setShowAuthModal(false);
    navigate('/reseau/connexion');
  };
  
  const goToVerification = () => {
    setShowAuthModal(false);
    navigate('/reseau/verification');
  };

  return (
    <DemoModeContext.Provider value={{ requireAuth, isAuthenticated }}>
      {children}
      
      <AnimatePresence>
        {showAuthModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAuthModal(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-full sm:max-w-[400px] sm:rounded-2xl rounded-t-3xl shadow-2xl relative overflow-hidden max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Orange accent line */}
              <div className="h-1 bg-[#E8650A]" />
              
              {/* Mobile drag indicator */}
              <div className="sm:hidden flex justify-center pt-3 pb-1">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              
              <div className="px-5 pb-6 pt-2 sm:p-6">
                {/* Close button */}
                <button
                  onClick={() => setShowAuthModal(false)}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={22} />
                </button>
                
                {/* ═══ MESSAGE CONTEXTUEL - IMPORTANT ═══ */}
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-4">
                  <p 
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                    className="text-[#E8650A] text-center text-[15px] font-medium"
                  >
                    Pour <span className="font-bold">{authAction}</span>,<br/>
                    crée ton compte gratuit !
                  </p>
                </div>
                
                {/* ═══ TITRE ═══ */}
                <h2 
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} 
                  className="text-[22px] sm:text-2xl font-bold text-[#1a2a4a] text-center mb-5"
                >
                  Rejoins la communauté<br/>
                  <span className="text-[#E8650A]">de Confiance</span>
                </h2>
                
                {/* ═══ BLOC CONFIANCE ═══ */}
                <div className="bg-[#1a2a4a] rounded-xl p-4 mb-5">
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="text-[#E8650A] flex-shrink-0" size={22} />
                    <span 
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} 
                      className="text-white font-bold text-base sm:text-lg"
                    >
                      1 personne = 1 compte
                    </span>
                  </div>
                  <p 
                    style={{ fontFamily: "'DM Sans', sans-serif" }} 
                    className="text-white/80 text-[13px] sm:text-sm leading-relaxed"
                  >
                    Zéro faux compte = Fini les arnaques et les faux profils.{' '}
                    <button 
                      onClick={goToVerification}
                      className="text-[#E8650A] font-semibold hover:underline inline"
                    >
                      En savoir plus
                    </button>
                  </p>
                </div>
                
                {/* ═══ LISTE AVANTAGES ═══ */}
                <ul className="space-y-2.5 mb-5">
                  {[
                    "Acheter à des professionnels sans commission",
                    "Trouver des artisans de confiance",
                    "Acheter, vendre, louer, échanger entre particuliers",
                    "Partager ta passion ou ton métier",
                    "Explorer 21 univers dans l'écosystème AbracadaWorld",
                    "Rencontrer et discuter avec des passionnés comme toi"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="w-5 h-5 mt-0.5 rounded-full bg-[#E8650A] flex items-center justify-center flex-shrink-0">
                        <Check size={11} className="text-white" strokeWidth={3} />
                      </span>
                      <span 
                        style={{ fontFamily: "'DM Sans', sans-serif" }} 
                        className="text-gray-700 text-[14px] sm:text-[15px] leading-snug"
                      >
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                
                {/* ═══ CTA PRINCIPAL ═══ */}
                <motion.button
                  onClick={goToRegister}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                  className="w-full py-4 bg-[#E8650A] hover:bg-[#d55a09] active:bg-[#c45008] text-white font-bold text-base sm:text-lg rounded-xl flex items-center justify-center gap-2 shadow-lg transition-colors"
                >
                  <UserPlus size={20} />
                  Créer mon compte gratuit
                </motion.button>
                
                {/* ═══ LIEN SECONDAIRE ═══ */}
                <button
                  onClick={goToLogin}
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                  className="w-full mt-3 py-3 text-[#1a2a4a] font-medium text-[15px] hover:underline active:text-[#E8650A]"
                >
                  J'ai déjà un compte
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </DemoModeContext.Provider>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// BOUTON PROTÉGÉ
// ══════════════════════════════════════════════════════════════════════════════

export function ProtectedButton({ 
  children, 
  onClick, 
  action = 'accéder à cette fonctionnalité',
  className = '',
  ...props 
}) {
  const { requireAuth } = useDemoMode();
  
  const handleClick = (e) => {
    if (requireAuth(action)) {
      onClick?.(e);
    }
  };
  
  return (
    <button onClick={handleClick} className={className} {...props}>
      {children}
    </button>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// LIEN PROTÉGÉ
// ══════════════════════════════════════════════════════════════════════════════

export function ProtectedLink({ 
  children, 
  to, 
  action = 'accéder à cette page',
  className = '',
  ...props 
}) {
  const { requireAuth } = useDemoMode();
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    e.preventDefault();
    if (requireAuth(action)) {
      navigate(to);
    }
  };
  
  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
}

export default DemoModeProvider;
